import { css, Global } from '@emotion/core';
import styled from '@emotion/styled';
import Typography from '@ep/components/Typography';
import theme from '@ep/theme';
import { Gcms_SubMenus } from '@ep/types/gatsbyTypes';
import useScrollCallback from '@ep/utils/useScrollCallback';
import 'normalize.css';
import React, { ReactNode, useState } from 'react';
import Theme from 'theme/theme';
import Breadcrumb from './Breadcrumb';
import Footer from './Footer';
import Header from './Header';
import NavBar from './NavBar';
import NavDrawer from './NavDrawer';
export interface LayoutProps {
  pathname: string;
  children: ReactNode;
  subMenu?: Gcms_SubMenus[];
  showBreadcrumb: boolean;
}

const AppBar = styled.div`
  box-shadow: ${theme.shadows[4]};
  position: fixed;
  width: 100%;
  background-color: ${theme.colors.white};
  z-index: ${theme.zIndex.header};
`;

const BreadcrumbWrapper = styled.div`
  height: ${({ height, collapsed }) => (collapsed ? 0 : height)};
  overflow: hidden;
  transition: ${theme.transitions.create(['height'])};
  ${theme.mediaQueries.desktopMenuBreak} {
    display: none;
    height: 0;
  }
`;

const Main = styled.main`
  position: relative;
  padding-top: ${(props) => (props.hasBreadcrumb ? `15rem` : `10rem`)};

  @media (max-width: 1170px) {
    padding-top: 6rem;
  }

  ${theme.mediaQueries.tablet} {
    padding-top: 5.25rem;
  }

  ${theme.mediaQueries.mobile} {
    padding-top: 3.5rem;
  }
`;

let position = typeof window !== 'undefined' ? window.pageYOffset : 0;

const Layout = ({
  pathname,
  children,
  subMenu,
  showBreadcrumb,
}: LayoutProps) => {
  const [scrollingDown, setScrollingDown] = useState(false);

  useScrollCallback(() => {
    setScrollingDown(window.pageYOffset > 64 && window.pageYOffset > position);
    position = window.pageYOffset;
  });

  const [drawerOpen, setDrawerOpen] = useState(false);

  function toggleDrawer() {
    setDrawerOpen(!drawerOpen);

    if (drawerOpen) {
      document.body.classList.remove('drawer-open');
    } else {
      document.body.classList.add('drawer-open');
    }
  }

  const currentUrl =
    typeof window !== 'undefined' ? window.location.pathname : '';
  var liveChat = (currentUrl === '/support/movie-magic-budgeting/') || (currentUrl === '/support/movie-magic-scheduling/');

  return (
    <>
      <Global
        styles={css`
          html {
            box-sizing: border-box;
            scroll-behavior: smooth;
          }

          * {
            box-sizing: inherit;
            -webkit-tap-highlight-color: transparent;
          }

          body.drawer-open {
            overflow: hidden;
          }

          .embeddedServiceHelpButton {
            right: 0 !important;
            left: 100vw !important;
          }
          .embeddedServiceSidebar .dockableContainer {
            right: auto !important;
            left: 3rem !important;
          }
          .embeddedServiceHelpButton .helpButton {
            display: ${liveChat ? 'block' : 'none'};
            margin-right: 3rem;
            ${Theme.mediaQueries.mobileAndTablet} {
              display: none;
            }
          }
          .embeddedServiceHelpButton .helpButton .uiButton {
            background-color: #1474d4;
            font-family: 'Salesforce Sans', sans-serif;
          }
          .embeddedServiceHelpButton .helpButton .uiButton:focus {
            outline: 1px solid #1474d4;
          }
          .embeddedServiceLiveAgentStateChatEventMessage .eventMessage, .embeddedServiceLiveAgentStateChatInputFooter .chasitorText, .embeddedServiceLiveAgentStateChatPlaintextMessageDefaultUI.agent.plaintextContent, .embeddedServiceLiveAgentStateChat .chatSessionStartTime, .embeddedServiceSidebarFormField .uiInput .uiLabel-left, .embeddedServiceSidebarFormField .slds-style-inputtext, .embeddedServiceSidebarFormField .slds-style-select{
            color: #00273f !important;
          }
          @font-face {
            font-family: 'Salesforce Sans';
            src: url('https://c1.sfdcstatic.com/etc/clientlibs/sfdc-aem-master/clientlibs_base/fonts/SalesforceSans-Regular.woff')
                format('woff'),
              url('https://c1.sfdcstatic.com/etc/clientlibs/sfdc-aem-master/clientlibs_base/fonts/SalesforceSans-Regular.ttf')
                format('truetype');
          }
        `}
      />
      <Typography />
      <AppBar>
        <Header
          scrollingDown={scrollingDown}
          drawerOpen={drawerOpen}
          toggleDrawer={toggleDrawer}
        />
        <nav>
          <NavDrawer open={drawerOpen} toggleDrawer={toggleDrawer} />
          <NavBar scrollingDown={scrollingDown} subMenu={subMenu} />
        </nav>
        {showBreadcrumb && (
          <BreadcrumbWrapper height="4rem" collapsed={scrollingDown}>
            <Breadcrumb pathname={pathname} />
          </BreadcrumbWrapper>
        )}
      </AppBar>
      <Main hasBreadcrumb={showBreadcrumb}>{children}</Main>
      <Footer />
    </>
  );
};

export default React.memo(Layout);
